// Image-file resolution for the pdf-editor images → PDF import flow.
//
// mupdf ingests PNG and JPEG byte streams; everything else the browser
// can decode (WebP, AVIF, GIF, BMP, SVG, HEIC where supported) is
// re-encoded through a canvas. Original PNG/JPEG bytes pass through
// UNTOUCHED (no recompression, full resolution) — except JPEGs whose
// EXIF orientation isn't upright: mupdf doesn't apply EXIF rotation,
// so those are redrawn via the canvas (browsers orient automatically)
// and re-encoded at q=0.92.

export const IMAGE_IMPORT_ACCEPT = [
  ".jpg", ".jpeg", ".png", ".webp", ".gif", ".bmp",
  ".avif", ".heic", ".heif", ".svg",
  "image/*",
].join(",");

const IMAGE_EXT_RE = /\.(jpe?g|png|webp|gif|bmp|avif|heic|heif|svg)$/i;

export function isImageImportFile(f: File): boolean {
  return IMAGE_EXT_RE.test(f.name) || f.type.startsWith("image/");
}

/** Strip the image extension for captions / the imported tab's name. */
export function imageBaseName(name: string): string {
  return name.replace(IMAGE_EXT_RE, "");
}

export interface ResolvedImportImage {
  /** Keys the layout ops and the worker's image map. */
  id: string;
  name: string;
  dataUrl: string;
  /** Natural CSS px. */
  width: number;
  height: number;
}

export const IMAGE_IMPORT_MAX_FILES = 200;
const MAX_FILE_BYTES = 40 * 1024 * 1024;
/** Longest side for the canvas re-encode path (passthrough keeps full
 *  resolution — this only bounds decode-and-redraw memory). */
const MAX_REENCODE_PX = 4096;

// ───────────────────────── EXIF orientation ─────────────────────────

/** EXIF orientation tag (0x0112) of a JPEG, 1 when absent/unreadable. */
export function jpegOrientation(bytes: Uint8Array): number {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return 1;
  let o = 2;
  while (o + 4 <= bytes.length) {
    if (bytes[o] !== 0xff) return 1;
    const marker = bytes[o + 1]!;
    if (marker === 0xda || marker === 0xd9) return 1; // image data / EOI
    const len = (bytes[o + 2]! << 8) | bytes[o + 3]!;
    if (len < 2) return 1;
    if (marker === 0xe1 && len >= 16) {
      const p = o + 4;
      // "Exif\0\0"
      if (
        bytes[p] === 0x45 && bytes[p + 1] === 0x78 &&
        bytes[p + 2] === 0x69 && bytes[p + 3] === 0x66 &&
        bytes[p + 4] === 0x00 && bytes[p + 5] === 0x00
      ) {
        const tiff = p + 6;
        const end = Math.min(bytes.length, o + 2 + len);
        if (tiff + 8 > end) return 1;
        const little = bytes[tiff] === 0x49;
        const u16 = (at: number) =>
          at + 2 <= end
            ? little
              ? bytes[at]! | (bytes[at + 1]! << 8)
              : (bytes[at]! << 8) | bytes[at + 1]!
            : 0;
        const u32 = (at: number) =>
          at + 4 <= end
            ? little
              ? (u16(at) | (u16(at + 2) << 16)) >>> 0
              : ((u16(at) << 16) | u16(at + 2)) >>> 0
            : 0;
        const ifd = tiff + u32(tiff + 4);
        const n = u16(ifd);
        for (let i = 0; i < n; i += 1) {
          const e = ifd + 2 + i * 12;
          if (e + 12 > end) break;
          if (u16(e) === 0x0112) {
            const v = u16(e + 8);
            return v >= 1 && v <= 8 ? v : 1;
          }
        }
      }
      return 1;
    }
    o += 2 + len;
  }
  return 1;
}

// ───────────────────────── decode helpers ─────────────────────────

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(new Error("read failed"));
    r.readAsDataURL(file);
  });
}

/** Decode via <img> — browsers apply EXIF orientation automatically,
 *  so naturalWidth/Height (and any canvas redraw) are upright. */
function decodeImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("not a decodable image"));
    };
    img.src = url;
  });
}

/** Photographic sources re-encode as JPEG; graphics keep PNG. */
function reencodeMime(name: string, type: string): string {
  return /\.(jpe?g|webp|avif|heic|heif)$/i.test(name) ||
    /jpe?g|webp|avif|hei[cf]/i.test(type)
    ? "image/jpeg"
    : "image/png";
}

let nextImageId = 0;

async function resolveOne(file: File): Promise<ResolvedImportImage> {
  if (file.size > MAX_FILE_BYTES) throw new Error("larger than 40 MB");
  const bytes = new Uint8Array(await file.arrayBuffer());
  const isPng =
    bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47;
  const isJpeg = bytes[0] === 0xff && bytes[1] === 0xd8;

  const img = await decodeImage(file);
  const width = img.naturalWidth;
  const height = img.naturalHeight;
  if (!width || !height) throw new Error("image has no size");
  const id = `img-${(nextImageId += 1)}`;

  if (isPng || (isJpeg && jpegOrientation(bytes) === 1)) {
    return { id, name: file.name, dataUrl: await fileToDataUrl(file), width, height };
  }

  // Canvas path: formats mupdf can't ingest, or EXIF-rotated JPEGs.
  const k = Math.min(1, MAX_REENCODE_PX / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * k));
  canvas.height = Math.max(1, Math.round(height * k));
  canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
  const mime = reencodeMime(file.name, file.type);
  return {
    id,
    name: file.name,
    dataUrl: canvas.toDataURL(mime, mime === "image/jpeg" ? 0.92 : undefined),
    width: canvas.width,
    height: canvas.height,
  };
}

export interface ResolvedImageBatch {
  images: ResolvedImportImage[];
  /** "name — reason" per file that couldn't be used. */
  failed: string[];
}

export async function resolveImageFiles(files: File[]): Promise<ResolvedImageBatch> {
  const images: ResolvedImportImage[] = [];
  const failed: string[] = [];
  for (const file of files.slice(0, IMAGE_IMPORT_MAX_FILES)) {
    try {
      images.push(await resolveOne(file));
    } catch (err) {
      failed.push(`${file.name} — ${err instanceof Error ? err.message : String(err)}`);
    }
  }
  for (const file of files.slice(IMAGE_IMPORT_MAX_FILES)) {
    failed.push(`${file.name} — over the ${IMAGE_IMPORT_MAX_FILES}-image limit`);
  }
  return { images, failed };
}
